import { useCallback, useState } from "react";
import { useProjects } from "@contexts/ProjectContext";
import type { Project } from "@shared/types";
import { buildProjectContext } from "@domain/project/ProjectContext";
import {
  callProjectAssistant,
  type ChatMessage,
} from "@data/functions/projectAssistant";
import {
  budgetRepository,
  phaseRepository,
  contractorRepository,
  floorplanRepository,
  designItemRepository,
  feedbackRepository,
  permitsChecklistRepository,
} from "@data/firestore";
import { planBSqFt, planBTotalSqFt, planBSpaces } from "@domain/budget/planB";
import { projectData } from "@domain/project/projectData";

export interface ChatTopic {
  title: string;
  body: string;
  containerLabel: string;
  severity?: "info" | "warn" | "risk";
  category?: string;
}

interface ProjectContextValue {
  activeProject: Project | null | undefined;
}

function newMessageId(): string {
  return (
    (typeof crypto !== "undefined" && crypto.randomUUID && crypto.randomUUID()) ||
    `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`
  );
}

export interface UseChatResult {
  messages: ChatMessage[];
  sending: boolean;
  error: string | null;
  topic: ChatTopic | null;
  setTopic: (next: ChatTopic | null) => void;
  /** Send the user message. If a topic is set, its content is prepended to
   *  the outgoing LLM context (invisible in the UI thread). */
  send: (content: string) => Promise<void>;
  clear: () => void;
}

/**
 * Per-project chat session (in-memory).
 */
export function useChat(): UseChatResult {
  const projects = useProjects() as ProjectContextValue;
  const activeProject = projects.activeProject;

  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [topic, setTopic] = useState<ChatTopic | null>(null);

  const send = useCallback(
    async (content: string) => {
      if (!activeProject) {
        setError("No active project");
        return;
      }
      if (!content.trim()) return;

      const userMessage: ChatMessage = {
        id: newMessageId(),
        role: "user",
        content,
        createdAt: Date.now(),
      };

      setMessages((prev) => [...prev, userMessage]);
      setSending(true);
      setError(null);

      try {
        const [
          budget,
          phases,
          contractors,
          floorplans,
          designItems,
          feedbackTickets,
          permitsChecklist,
        ] = await Promise.all([
          budgetRepository.get(activeProject),
          phaseRepository.get(activeProject),
          contractorRepository.get(activeProject),
          floorplanRepository.get(activeProject),
          designItemRepository.get(activeProject),
          feedbackRepository.get(activeProject),
          permitsChecklistRepository.get(activeProject),
        ]);

        const context = buildProjectContext({
          project: activeProject,
          budget,
          phases,
          contractors: contractors as unknown as Array<Record<string, unknown>>,
          floorplans: floorplans.map((f) => ({
            name: f.name,
            ext: f.ext,
            size: f.size,
          })),
          designItems: designItems as unknown as Array<Record<string, unknown>>,
          feedbackTickets: feedbackTickets.map((t) => ({
            status: (t as { status?: string }).status,
          })),
          permitsChecklist,
          sqFt: {
            current: projectData.currentSqFt,
            expansion: planBSqFt,
            total: planBTotalSqFt,
          },
          spaces: planBSpaces,
        });

        // Compose outgoing history. If a topic is active, prepend a
        // synthetic user turn describing it — LLM sees it as context,
        // UI does not render it.
        const historyForFlow: Array<{ role: "user" | "assistant"; content: string }> = [];
        if (topic) {
          historyForFlow.push({
            role: "user",
            content:
              `[Discussion topic I raised about ${topic.containerLabel}]\n` +
              `**${topic.title}**\n${topic.body}\n\n` +
              `Please keep your replies focused on this topic unless I explicitly change subjects.`,
          });
        }
        for (const m of [...messages, userMessage]) {
          historyForFlow.push({ role: m.role, content: m.content });
        }

        const result = await callProjectAssistant({
          ownerUid: activeProject.ownerUid,
          projectId: activeProject.id,
          context,
          messages: historyForFlow,
        });

        const assistantMessage: ChatMessage = {
          id: newMessageId(),
          role: "assistant",
          content: result.reply,
          createdAt: Date.now(),
        };
        setMessages((prev) => [...prev, assistantMessage]);
      } catch (err) {
        console.error("[useChat]", err);
        const message = (err as { message?: string })?.message ?? String(err);
        setError(message);
      } finally {
        setSending(false);
      }
    },
    [activeProject, messages, topic]
  );

  const clear = useCallback(() => {
    setMessages([]);
    setTopic(null);
    setError(null);
  }, []);

  return { messages, sending, error, topic, setTopic, send, clear };
}
